import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Observable, finalize, forkJoin } from 'rxjs';
import {
  BillingApiService,
  BillingSummary,
  BillingTransaction,
} from 'src/app/core/services/billing-api.service';
import { TenantOnboardingApiService } from 'src/app/core/services/tenant-onboarding-api.service';
import type { TenantRow } from 'src/app/core/services/tenant-onboarding-api.service';
import {
  ListCellDefDirective,
  ListModuleColumnDef,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
  ListModuleTableComponent,
} from 'src/app/shared/components/list-module';
import { PdfPreviewModalComponent } from 'src/app/shared/components/pdf-preview-modal/pdf-preview-modal.component';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';

@Component({
  selector: 'app-billing',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ListModuleTableComponent,
    ListModuleSurfaceComponent,
    ListCellDefDirective,
    PageSurfaceHeaderComponent,
    PdfPreviewModalComponent,
  ],
  templateUrl: './billing.component.html',
})
export class BillingComponent implements OnInit {
  private readonly api = inject(BillingApiService);
  private readonly tenantApi = inject(TenantOnboardingApiService);

  clients: TenantRow[] = [];
  selectedTenantId: number | null = null;

  /**
   * Prepaid clients buy a plan up front and draw usage against its quota, so there is nothing to
   * invoice in arrears. The server refuses it too — this keeps the operator from filling in a
   * form whose only outcome is an error.
   */
  get isPrepaidClient(): boolean {
    const client = this.clients.find((c) => c.id === this.selectedTenantId);
    return (client?.billing_type ?? '').toUpperCase() === 'PREPAID';
  }

  summary: BillingSummary | null = null;
  transactions: BillingTransaction[] = [];
  loading = false;
  actionMsg = '';
  actionErr = '';

  readonly txnColumns: ListModuleColumnDef[] = [
    { id: 'date', header: 'Date' },
    { id: 'product', header: 'Product' },
    { id: 'session', header: 'Session' },
    { id: 'amount', header: 'Amount' },
    { id: 'status', header: 'Status' },
  ];

  // Generate-invoice form
  periodStart = '';
  periodEnd = '';
  gstPercent: number | null = 18;
  notes = '';
  generating = false;
  generatedForTenantId: number | null = null;

  preview: { title: string; filename: string; load: () => Observable<Blob> } | null = null;

  /** Draft preview needs a client, a valid period, and something to bill. */
  get canPreviewDraft(): boolean {
    return !!this.selectedTenantId && !!this.periodStart && !!this.periodEnd && !this.generating && (this.summary?.pending_count ?? 0) > 0;
  }

  /** Toolbar: client filter only (custom via listToolbar slot). */
  readonly listSurfaceConfig: ListModuleSurfaceConfig = {
    toolbar: {},
    pagination: false,
  };

  ngOnInit(): void {
    this.tenantApi.listTenants({ page: 1, page_size: 500 }).subscribe({
      next: (res) => (this.clients = res?.data?.result ?? []),
    });
    const now = new Date();
    this.periodStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    this.periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  }

  clientLabel(t: TenantRow): string {
    return t.tenant_name ?? t.unique_id ?? `Client #${t.id}`;
  }

  onTenantChange(): void {
    this.actionMsg = '';
    this.actionErr = '';
    this.generatedForTenantId = null;
    this.load();
  }

  load(): void {
    if (!this.selectedTenantId) return;
    const tid = this.selectedTenantId;
    this.loading = true;
    forkJoin({
      summary: this.api.tenantSummary(tid),
      txns: this.api.listTransactions({ tenant_id: tid, page_size: 100 }),
    })
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (r) => {
          this.summary = r.summary?.data ?? null;
          this.transactions = r.txns?.data?.items ?? [];
        },
        error: () => (this.actionErr = 'Could not load billing data.'),
      });
  }

  generate(): void {
    if (!this.selectedTenantId || !this.periodStart || !this.periodEnd) return;
    const tenantId = this.selectedTenantId;
    this.generating = true;
    this.actionMsg = '';
    this.actionErr = '';
    this.generatedForTenantId = null;
    this.api
      .generateInvoice(tenantId, {
        period_start: new Date(this.periodStart).toISOString(),
        period_end: new Date(this.periodEnd + 'T23:59:59').toISOString(),
        gst_percent: this.gstPercent ?? undefined,
        notes: this.notes || undefined,
      })
      .pipe(finalize(() => (this.generating = false)))
      .subscribe({
        next: (res) => {
          this.actionMsg = res.message;
          this.generatedForTenantId = tenantId;
          this.load();
        },
        error: (err) => (this.actionErr = err?.error?.message ?? 'Could not generate invoice.'),
      });
  }

  previewDraft(): void {
    if (!this.canPreviewDraft || !this.selectedTenantId) return;
    const body = {
      tenant_id: this.selectedTenantId,
      period_start: new Date(this.periodStart).toISOString(),
      period_end: new Date(this.periodEnd + 'T23:59:59').toISOString(),
      gst_percent: this.gstPercent ?? undefined,
      notes: this.notes || undefined,
    };
    this.preview = { title: 'Draft invoice (not issued)', filename: 'invoice-draft.pdf', load: () => this.api.previewDraftInvoice(body) };
  }

  closePreview(): void {
    this.preview = null;
  }

  statusClass(status: string): string {
    switch (status) {
      case 'PAID':
        return 'bg-success/20 text-success';
      case 'SENT':
        return 'bg-primary/10 text-primary';
      case 'VOID':
        return 'bg-danger/20 text-danger';
      default:
        return 'bg-warning/20 text-warning';
    }
  }
}
